import React, { useState, useEffect, useContext } from "react";
import api from "../../api";
import ReactQuill, { Quill } from "react-quill";
import "react-quill/dist/quill.snow.css";
import "../../css/quill-fonts.css";
import AuthContext from "../../context/AuthContext";

/* ----------------------------------------
   🔥 Register Custom Fonts (Same as Blog Page)
---------------------------------------- */
const Font = Quill.import("formats/font");
Font.whitelist = [
  "arial",
  "times-new-roman",
  "georgia",
  "courier-new",
  "verdana",
];
Quill.register(Font, true);

/* ----------------------------------------
   🔥 Full Toolbar Options (Same as Blog Page)
---------------------------------------- */
const toolbarOptions = [
  [{ font: ["arial", "times-new-roman", "georgia", "courier-new", "verdana"] }],
  [{ size: ["small", false, "large", "huge"] }],
  [{ header: [1, 2, 3, 4, 5, 6, false] }],
  ["bold", "italic", "underline", "strike"],
  [{ color: [] }, { background: [] }],
  [{ align: [] }],
  ["blockquote", "code-block"],
  [{ list: "ordered" }, { list: "bullet" }],
  [{ script: "sub" }, { script: "super" }],
  ["link", "image", "video"],
  ["clean"],
];

const modules = { toolbar: { container: toolbarOptions } };

const formats = [
  "font",
  "size",
  "header",
  "bold",
  "italic",
  "underline",
  "strike",
  "color",
  "background",
  "align",
  "blockquote",
  "code-block",
  "list",
  "bullet",
  "script",
  "link",
  "image",
  "video",
];

const presetHeadings = ["Introduction", "Discussion", "Conclusion", "References"];

/* ----------------------------------------
   MAIN COMPONENT
---------------------------------------- */
const AdminResearch = () => {
  const [title, setTitle] = useState("");
  const [abstractText, setAbstractText] = useState("");
  const [keywords, setKeywords] = useState("");
  const [sections, setSections] = useState([]);
  const [customHeading, setCustomHeading] = useState("");
  const [researchers, setResearchers] = useState([{ name: "", qualification: "" }]);
  const [papers, setPapers] = useState([]);
  const [statusUpdateLoading, setStatusUpdateLoading] = useState(false);
  const { user } = useContext(AuthContext);

  /* ----------------------------------------
     Fetch All Papers
  ---------------------------------------- */
  const fetchAllPapers = async () => {
    try {
      const res = await api.get("/api/research-papers/admins/all");
      setPapers(res.data);
    } catch (err) {
      console.error("Error fetching admin papers:", err);
    }
  };

  useEffect(() => {
    fetchAllPapers();
  }, []);

  /* ----------------------------------------
     Add Section
  ---------------------------------------- */
  const addSection = (heading) => {
    setSections([...sections, { heading, content: "" }]);
  };

  /* ----------------------------------------
     Add Researcher
  ---------------------------------------- */
  const addResearcher = () => {
    setResearchers([...researchers, { name: "", qualification: "" }]);
  };

  /* ----------------------------------------
     Submit Paper (Auto Approve)
  ---------------------------------------- */
  const handleSubmit = async (e) => {
    e.preventDefault();

    const email = user?.username;

    const paper = {
      title,
      abstractText,
      keywords,
      sectionsJson: JSON.stringify(sections),
      researchersJson: JSON.stringify(researchers),
    };

    try {
      await api.post("/api/research-papers/admins", paper, {
        params: { email },
      });

      alert("Research Paper Added (Auto Approved)!");

      setTitle("");
      setAbstractText("");
      setKeywords("");
      setSections([]);
      setResearchers([{ name: "", qualification: "" }]);
      fetchAllPapers();
    } catch (err) {
      console.error("Error adding admin paper:", err);
    }
  };

  /* ----------------------------------------
     Update Status
  ---------------------------------------- */
  const handleStatusUpdate = async (id, newStatus) => {
    try {
      setStatusUpdateLoading(true);
      await api.put(`/api/research-papers/admins/updatestatus/${id}?status=${newStatus}`);
      fetchAllPapers();
    } catch (err) {
      console.error("Error updating status:", err);
    } finally {
      setStatusUpdateLoading(false);
    }
  };

  /* ----------------------------------------
     RENDER UI
  ---------------------------------------- */
  return (
    <div className="container mt-4">
      <h2>Admin — Manage Research Papers</h2>

      {/* ADD NEW PAPER */}
      <form onSubmit={handleSubmit}>
        <div>
          <label>Title</label>
          <input
            type="text"
            className="form-control"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            required
          />
        </div>

        {/* FULL TOOLBAR FOR ABSTRACT */}
        <div>
          <label>Abstract</label>
          <ReactQuill
            theme="snow"
            value={abstractText}
            onChange={setAbstractText}
            modules={modules}
            formats={formats}
            style={{ marginBottom: "20px",   maxWidth: "100%",
    overflowX: "auto",
    wordBreak: "break-word",
    whiteSpace: "normal" }}
          />
        </div>

        <div>
          <label>Keywords</label>
          <input
            type="text"
            className="form-control"
            value={keywords}
            onChange={(e) => setKeywords(e.target.value)}
          />
        </div>

        {/* RESEARCHERS */}
        <h4>Researchers</h4>
        {researchers.map((resItem, idx) => (
          <div key={idx} style={{ marginBottom: "10px", position: "relative" }}>
            <button
              type="button"
              onClick={() =>
                setResearchers(researchers.filter((_, index) => index !== idx))
              }
              style={{
                position: "absolute",
                right: "-10px",
                top: "-10px",
                background: "red",
                color: "white",
                border: "none",
                borderRadius: "50%",
                width: "25px",
                height: "25px",
                cursor: "pointer",
              }}
            >
              ✕
            </button>

            <input
              type="text"
              placeholder="Name"
              className="form-control mb-1"
              value={resItem.name}
              onChange={(e) => {
                const newRes = [...researchers];
                newRes[idx].name = e.target.value;
                setResearchers(newRes);
              }}
            />

            <input
              type="text"
              placeholder="Qualification & Institution"
              className="form-control"
              value={resItem.qualification}
              onChange={(e) => {
                const newRes = [...researchers];
                newRes[idx].qualification = e.target.value;
                setResearchers(newRes);
              }}
            />
          </div>
        ))}

        <button type="button" className="btn btn-secondary mb-2" onClick={addResearcher}>
          Add Researcher
        </button>

        {/* SECTIONS */}
        <h4>Sections</h4>

        {sections.map((sec, idx) => (
          <div key={idx} className="my-2" style={{ position: "relative" }}>
            <button
              type="button"
              onClick={() =>
                setSections(sections.filter((_, index) => index !== idx))
              }
              style={{
                position: "absolute",
                right: "-10px",
                top: "-10px",
                background: "red",
                color: "white",
                border: "none",
                borderRadius: "50%",
                width: "25px",
                height: "25px",
                cursor: "pointer",
              }}
            >
              ✕
            </button>

            <h5>{sec.heading}</h5>

            {/* FULL TOOLBAR FOR EACH SECTION */}
            <ReactQuill
              theme="snow"
              value={sec.content}
              onChange={(val) => {
                const updated = [...sections];
                updated[idx].content = val;
                setSections(updated);
              }}
              modules={modules}
              formats={formats}
            />
          </div>
        ))}

        {/* SECTION SELECTOR */}
        <select
          className="form-control mt-3"
          onChange={(e) => {
            const value = e.target.value;

            if (value === "CUSTOM") {
              const custom = prompt("Enter your custom section heading:");

              if (custom && custom.trim() !== "") {
                addSection(custom.trim());
              }
            } else if (value) {
              addSection(value);
            }

            e.target.value = "";
          }}
        >
          <option value="">➕ Add New Section</option>
          {presetHeadings.map((h) => (
            <option key={h} value={h}>
              {h}
            </option>
          ))}
          <option value="CUSTOM">CUSTOM</option>
        </select>

        <button type="submit" className="btn btn-primary mt-3">
          Submit
        </button>
      </form>

      <hr />

    
    </div>
  );
};

export default AdminResearch;
